import { BYOItem, BYOStepId } from 'api/contentful/contentTypes.cloverdotcom';
import cx from 'classnames';
import { ImageModal } from 'components/ImageModal/ImageModal';
import { TextV2 } from 'components/TextV2/TextV2';
import { isMultipleQuantityAllowed } from 'features/build-your-own/hooks/sub-hooks/utils';
import { useBuildYourOwn } from 'features/build-your-own/hooks/useBuildYourOwn';
import React, { FC, useState } from 'react';
import styles from './BYOItemTile.module.scss';
import { ItemContainer, ItemImage, ItemInfoAndActions, ItemTitles } from './components';
import { BYOItemPricing } from 'features/build-your-own/components/BYOPricing/BYOItemPricing';
import { useDCSProductsQuery } from 'hooks/DCS/useDCSProductsQuery';
import { areBYOProductsSelfServiceEnabled } from 'features/build-your-own/components/BYOPricing/BYOPricingUtils';
import { BooleanFilter } from 'utils/TypeUtils';
import { getContentTypeFromContentfulProductsOrSoftware } from 'features/build-your-own/hooks/utils';
import { Icon, IconType } from 'components/Icon/Icon';

interface IBYOItemProps extends BYOItem {
  isImageClickable?: boolean;
}

export const BYOItemTile: FC<IBYOItemProps> = item => {
  const {
    images,
    headlineTitle,
    secondaryTitle,
    blurb,
    isAddButtonShown,
    itemKey,
    isImageClickable = false,
    products,
    features,
    selectButtonText,
    selectedButtonText,
    moreInfo,
  } = item;

  const {
    handleItemUpdate,
    currentStepId,
    isItemSelected,
    itemQuantity,
    addItemText,
    addItemIcon,
    decrementItemText,
    decrementItemIcon,
    selectedItemText,
    selectedItemCustomIcon,
    currentStepData,
  } = useBuildYourOwn();

  const { data: dcsProducts } = useDCSProductsQuery();
  const areProductsSelfServiceEnabled = areBYOProductsSelfServiceEnabled(dcsProducts, products);

  const image = images?.[0];

  const [showImageModal, setShowImageModal] = useState(false);
  const [showMoreInfo, setShowMoreInfo] = useState(false);

  const isBusinessOfferingStep = currentStepId === BYOStepId.BUSINESS_OFFERING;
  const isSelected = !!currentStepId && !!itemKey && isItemSelected(currentStepId, itemKey);
  const quantity = currentStepId && itemKey ? itemQuantity(currentStepId, itemKey) : 0;
  const dcsIds = products?.map(product => product.dcsId).filter(BooleanFilter) ?? [];
  const contentType = getContentTypeFromContentfulProductsOrSoftware(products);

  const primaryActionButton =
    isSelected && currentStepId === BYOStepId.POS_NEEDS
      ? { text: selectedItemText, imageEntry: selectedItemCustomIcon }
      : { text: addItemText, imageEntry: addItemIcon };

  const secondaryActionButton = {
    text: decrementItemText,
    imageEntry: decrementItemIcon,
  };

  const handleImageClick = () => {
    setShowImageModal(true);
  };

  const handleItemUpdateClick = (isRemoval = false) => {
    if (!currentStepId) return;

    handleItemUpdate(currentStepId, item, isRemoval);
  };

  return (
    <>
      <ItemContainer
        itemKey={itemKey}
        isBusinessOfferingStep={isBusinessOfferingStep}
        isAddButtonShown={!!isAddButtonShown}
        isSelected={isSelected}
        addItemIcon={addItemIcon}
        selectedItemCustomIcon={selectedItemCustomIcon}
        handleClick={() => handleItemUpdateClick()}
        ariaLabel={headlineTitle}
      >
        <ItemImage
          isImageClickable={isImageClickable}
          isFirstStep={!isAddButtonShown}
          image={image}
          handleImageClick={handleImageClick}
        />
        <div
          className={cx(styles.contentContainer, {
            [styles.contentContainerOpenedDrawerHeight]: showMoreInfo,
          })}
        >
          <div className={cx(styles.titleAndPricingContainer, { [styles.firstStep]: !isAddButtonShown })}>
            <ItemTitles headlineTitle={headlineTitle} secondaryTitle={secondaryTitle} />
            {isAddButtonShown && <BYOItemPricing dcsIds={dcsIds} contentType={contentType} />}
          </div>
          {blurb && <TextV2 value={blurb} />}
          <div className={styles.drawerButton}>
            {features && (
              <button onClick={() => setShowMoreInfo(!showMoreInfo)} aria-label="expand or collapse">
                <div
                  className={cx(styles.moreOrLessButtons, {
                    [styles.moreOrLessButtonsExpanded]: showMoreInfo,
                  })}
                >
                  <p>{showMoreInfo ? currentStepData?.collapseItemText : currentStepData?.expandItemText}</p>
                  <Icon type={showMoreInfo ? IconType.CHEVRON_UP : IconType.CHEVRON_DOWN} />
                </div>
              </button>
            )}
          </div>
        </div>
        {itemKey && (
          <ItemInfoAndActions
            itemKey={itemKey}
            isAddButtonShown={isAddButtonShown}
            areProductsOutOfStock={!areProductsSelfServiceEnabled}
            primaryActionButton={primaryActionButton}
            secondaryActionButton={secondaryActionButton}
            includedFeatures={features}
            itemQuantity={quantity}
            isMultipleQuantityAllowed={isMultipleQuantityAllowed(currentStepId)}
            handleActionClick={handleItemUpdateClick}
            isSelected={isSelected}
            showMoreInfo={showMoreInfo}
            selectButtonText={selectButtonText}
            selectedButtonText={selectedButtonText}
            moreInfo={moreInfo}
          />
        )}
      </ItemContainer>
      <ImageModal images={images} modalOpen={showImageModal} closeModal={() => setShowImageModal(false)} />
    </>
  );
};
